import { notification } from "antd"
import { useMutation, useQuery, useQueryClient } from "react-query"
import { updateBudget, updateProjectItemPricing } from "../Budgets/budgetApi"
import { useBudgetsInvalidator } from "../Budgets/budgetQueries"
import { postCatalogItem } from "../ItemCatalog/api"
import { useInvalidateItemCatalog } from "../ItemCatalog/apiQueries"
import { CatalogItemFormValues } from "../ItemCatalog/catalogItemModel"
import {
  WorksheetsQueryKey,
  copyWorksheetById,
  deleteWorksheetById,
  getWorksheetById,
  getWorksheets,
  saveWorksheet
} from "./BudgetCalculatorApi"
import { ApplyWorksheetRequest, ApplyWorksheetResult, applyFailureMessage } from "./applyWorksheetModel"
import { setAppliedYear } from "./budgetApi"
import { Worksheet } from "./budgetCalculatorModel"

export const useWorksheetsInvalidator = () => {
  const queryClient = useQueryClient()
  return {
    invalidateAll: () => queryClient.invalidateQueries(WorksheetsQueryKey),
    cacheWorksheet: (worksheet: Worksheet) =>
      queryClient.setQueryData([WorksheetsQueryKey, worksheet.budgetWorksheetId], worksheet)
  }
}

export const useWorksheetsQuery = () => {
  return useQuery(WorksheetsQueryKey, getWorksheets, { refetchOnWindowFocus: false })
}

export const useWorksheetQuery = (worksheetId: string | undefined, onLoaded: (worksheet: Worksheet) => void) => {
  return useQuery([WorksheetsQueryKey, worksheetId], () => getWorksheetById(worksheetId), {
    onSuccess: onLoaded,
    refetchOnWindowFocus: false
  })
}

export const useSaveWorksheetMutation = (onSaved: () => void, onFailed: () => void) => {
  const worksheets = useWorksheetsInvalidator()

  return useMutation((worksheet: Worksheet) => saveWorksheet(worksheet), {
    onSuccess: (_, worksheet) => {
      worksheets.cacheWorksheet(worksheet)
      onSaved()
    },
    onError: () => {
      onFailed()
      notification.error({ message: 'Your data could not be saved at this time. Please try again in a few minutes.' })
    }
  })
}

type CopyWorksheetData = {
  worksheetId: string
  title: string
}

export const useCopyWorksheetMutation = (onCopied: (worksheet: Worksheet) => void) => {
  const worksheets = useWorksheetsInvalidator()

  return useMutation(({ worksheetId, title }: CopyWorksheetData) => copyWorksheetById(worksheetId, title), {
    onSuccess: (worksheet) => {
      worksheets.cacheWorksheet(worksheet)
      worksheets.invalidateAll()
      onCopied(worksheet)
    },
    onError: () => {
      notification.error({ message: 'A copy could not be made at this time. Please try again in a few minutes.' })
    }
  })
}

export const useDeleteWorksheetMutation = () => {
  const worksheets = useWorksheetsInvalidator()

  return useMutation((worksheetId: string) => deleteWorksheetById(worksheetId), {
    onSuccess: () => {
      worksheets.invalidateAll()
    },
    onError: () => {
      notification.error({ message: 'Unable to delete a worksheet at this time. Please try again in a few minutes.' })
    }
  })
}

const priceLaborItems = async (laborItems: CatalogItemFormValues[]): Promise<number> => {
  let priced = 0
  for (const item of laborItems) {
    const succeeded = await postCatalogItem(item).then(() => true, () => false)
    if (succeeded) {
      priced += 1
    }
  }
  return priced
}

const markWorksheetApplied = (budgetWorksheetId: string, year: number): Promise<boolean> => {
  return setAppliedYear(budgetWorksheetId, year).then(() => true, () => false)
}

const repriceUnsoldJobs = (year: number): Promise<boolean> => {
  return updateProjectItemPricing(year).then(() => true, () => false)
}

export const useApplyWorksheetMutation = () => {
  const budgets = useBudgetsInvalidator()
  const worksheets = useWorksheetsInvalidator()
  const invalidateItemCatalog = useInvalidateItemCatalog()

  return useMutation({
    mutationFn: async (request: ApplyWorksheetRequest): Promise<ApplyWorksheetResult> => {
      await updateBudget(request.budgets)

      return {
        laborItemsAttempted: request.laborItems.length,
        laborItemsPriced: await priceLaborItems(request.laborItems),
        markedApplied: await markWorksheetApplied(request.budgetWorksheetId, request.year),
        repricingFailed: request.updateUnsoldJobs && !await repriceUnsoldJobs(request.year)
      }
    },
    onSettled: () => {
      budgets.invalidateAll()
      invalidateItemCatalog()
      worksheets.invalidateAll()
    },
    onError: (error: any) => {
      notification.error({ message: error.response?.data?.message ?? applyFailureMessage })
    }
  })
}
